import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { ServiceRows, ServiceTotals, svcMonthly, svcOneTime, normalizeServices, newService, fromCatalog } from "@/components/ContractForm";
import { CatalogPicker, useCatalog } from "@/components/ServiceCatalog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, PencilLine } from "@phosphor-icons/react";

export default function AmendDialog({ contract, onOpenChange, onSaved }) {
  const [services, setServices] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);
  const catalog = useCatalog(!!contract);

  useEffect(() => {
    if (contract) { setServices((contract.services || []).map((s) => ({ ...s }))); setRemarks(""); }
  }, [contract]);

  const before = svcMonthly(contract?.services || []);
  const after = svcMonthly(services);
  const newOneTime = svcOneTime(services.filter((s) => s._new));

  const save = async () => {
    if (!services.some((s) => s.description)) { toast.error("Le contrat doit conserver au moins un service."); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/contracts/${contract.id}/amend`, { services: normalizeServices(services).map(({ _new, ...s }) => s), remarks: remarks || null });
      const last = (data.amendments || []).slice(-1)[0];
      toast.success(`Amendement ${data.amendment_no} enregistré — facturation mensuelle mise à jour${last?.invoice_number ? ` ; facture ${last.invoice_number} générée (frais non-récurrents)` : ""}`);
      onOpenChange(false); onSaved?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!contract} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="amend-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><PencilLine size={20} /> Amender le contrat {contract?.contract_number}</DialogTitle>
          <DialogDescription>Ajoutez ou modifiez des services en cours de contrat. La récurrence mensuelle est mise à jour immédiatement et les frais non-récurrents des nouveaux services (surlignés) génèrent une facture brouillon.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <ServiceRows services={services} setServices={setServices} onCatalogSaved={catalog.reload} />
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex gap-2 flex-wrap">
              <Button data-testid="amend-add-service-btn" variant="outline" size="sm" className="rounded-sm" onClick={() => setServices([...services, { ...newService(), _new: true }])}><Plus size={14} className="mr-1" /> Ajouter un service</Button>
              <CatalogPicker items={catalog.items} onPick={(it) => setServices([...services, { ...fromCatalog(it), _new: true }])} />
            </div>
            <ServiceTotals services={services} />
          </div>
          <div className="border border-border rounded-sm p-4 bg-muted/30 text-sm flex flex-wrap gap-6" data-testid="amend-summary">
            <span>Mensuel actuel : <strong className="tabular-nums">{money(before)}</strong></span>
            <span>Nouveau mensuel : <strong className="tabular-nums" data-testid="amend-after">{money(after)}</strong> <span className={after >= before ? "text-emerald-700" : "text-red-600"}>({after >= before ? "+" : ""}{money(after - before)})</span></span>
            <span>Frais non-récurrents à facturer : <strong className="tabular-nums" data-testid="amend-onetime">{money(newOneTime)}</strong></span>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Remarque de l'amendement (imprimée sur le contrat)</Label>
            <Textarea data-testid="amend-remarks" className="rounded-sm" value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Ex. Ajout de 5 lignes supplémentaires à la demande du client" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button data-testid="save-amend-btn" className="rounded-sm" onClick={save} disabled={saving}>{saving ? "Enregistrement…" : "Enregistrer l'amendement"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
